import moment from "moment";
import type { DriverListData } from "../redux/onBoardingSlice.types";
import { DriverStatus } from "../../constants/OnBoardingConstants";

// ── Types ──────────────────────────────────────────────────────────────────

interface DropdownFilterItem {
  value: string;
}

interface DateRangeValue {
  startDate: Date | string;
  endDate: Date | string;
}

export interface OnBoardingFilters {
  pageSize?: number;
  status?: { key?: string; value: DropdownFilterItem[] };
  nationality?: { key?: string; value: DropdownFilterItem[] };
  vehicleType?: { key?: string; value: DropdownFilterItem[] };
  dateRange?: { key?: string; value: DateRangeValue };
  searchAttribute?: { key: string; value: string };
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Returns true for driver statuses that are terminal (cannot be reviewed).
 * Pure function — safe to unit-test without any React context.
 */
export const isTerminalDriverStatus = (status: string): boolean =>
  status === DriverStatus.APPROVED || status === DriverStatus.REJECTED;

// ── Transforms ─────────────────────────────────────────────────────────────

export const createOnBoardingFilterPayload = (
  filters: OnBoardingFilters,
  driverList: Pick<DriverListData, "pageNumber" | "totalPages"> | null,
  isLoadMore?: boolean
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    pageNumber: 0,
    pageSize: filters.pageSize ?? 20,
  };

  if (isLoadMore && driverList) {
    payload.pageNumber =
      driverList.pageNumber + 1 < driverList.totalPages
        ? driverList.pageNumber + 1
        : driverList.pageNumber;
  }

  // Build attributes array — only included when filters have actual selections
  const attributes: { key: string; value: string[] }[] = [];

  if (filters.status?.value?.length) {
    attributes.push({ key: "status", value: filters.status.value.map((item) => item.value) });
  }

  if (filters.nationality?.value?.length) {
    attributes.push({ key: "nationality", value: filters.nationality.value.map((item) => item.value) });
  }

  if (filters.vehicleType?.value?.length) {
    attributes.push({ key: "vehicleType", value: filters.vehicleType.value.map((item) => item.value) });
  }

  if (attributes.length) {
    payload.attributes = attributes;
  }

  if (filters.dateRange?.value?.startDate && filters.dateRange?.value?.endDate) {
    payload.dateRangeFilter = {
      key: "submissionDate",
      fromDate: moment(filters.dateRange.value.startDate, "DD/MM/YYYY").format("YYYY-MM-DD"),
      toDate: moment(filters.dateRange.value.endDate, "DD/MM/YYYY").format("YYYY-MM-DD"),
    };
  }

  if (filters.searchAttribute?.key && filters.searchAttribute?.value) {
    payload.searchAttribute = {
      key: filters.searchAttribute.key,
      value: filters.searchAttribute.value,
    };
  }

  return payload;
};
